/**
 * Advanced Error Handler
 * Provides comprehensive error handling for the application
 * @module utils/advancedErrorHandler
 */

import { getErrorMessage } from './errorHandler';
import { trackError } from '@/utils/monitoring';

/**
 * Error Types
 */
export const ErrorTypes = {
  NETWORK: 'NETWORK_ERROR',
  AUTH: 'AUTH_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  SERVER: 'SERVER_ERROR',
  CLIENT: 'CLIENT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

/**
 * Get error type from error object
 * @param {Error|Object} error - Error object
 * @returns {string} - Error type
 */
export const getErrorType = (error) => {
  if (!error) return ErrorTypes.UNKNOWN;

  // Network errors
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return ErrorTypes.NETWORK;
  }

  // Auth errors
  if (error.response?.status === 401 || error.response?.status === 403) {
    return ErrorTypes.AUTH;
  }

  // Validation errors
  if (error.response?.status === 400 || error.response?.status === 422) {
    return ErrorTypes.VALIDATION;
  }

  // Server errors
  if (error.response?.status >= 500) {
    return ErrorTypes.SERVER;
  }

  // Client errors
  if (error.response?.status >= 400 && error.response?.status < 500) {
    return ErrorTypes.CLIENT;
  }

  return ErrorTypes.UNKNOWN;
};

/**
 * Check if error should be shown to user
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const shouldShowError = (error) => {
  const errorType = getErrorType(error);
  
  // Don't show auth errors if user is not logged in (handled by auth flow)
  if (errorType === ErrorTypes.AUTH) {
    const token = localStorage.getItem('token');
    return !!token; // Only show if user was logged in
  }

  // Show all other errors
  return true;
};

/**
 * Handle error with advanced logic
 * @param {Error|Object} error - Error object
 * @param {Object} options - Error handling options
 * @returns {Object} - Error information
 */
export const handleAdvancedError = (error, options = {}) => {
  const {
    showNotification = true,
    logError = true,
    trackError: shouldTrack = true,
    fallbackMessage = 'حدث خطأ غير متوقع',
    context = {},
  } = options;

  const errorType = getErrorType(error);
  const userMessage = getErrorMessage(error) || fallbackMessage;
  const shouldShow = shouldShowError(error);

  // Log error
  if (logError) {
    console.error('🚨 Error Details:', {
      type: errorType,
      message: error.message || userMessage,
      response: error.response?.data,
      status: error.response?.status,
      context,
    });
  }

  // Track error
  if (shouldTrack) {
    trackError(error, {
      type: errorType,
      context,
      userMessage,
    });
  }

  // Return error information
  return {
    type: errorType,
    message: userMessage,
    shouldShow,
    originalError: error,
    status: error.response?.status,
    data: error.response?.data,
  };
};

/**
 * Wrap async function with advanced error handling
 * @param {Function} asyncFn - Async function to wrap
 * @param {Object} options - Error handling options
 * @returns {Promise}
 */
export const withErrorHandling = (asyncFn, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const errorInfo = handleAdvancedError(error, {
        ...options,
        context: { function: asyncFn.name, args },
      });

      // Re-throw if needed
      if (options.rethrow !== false) {
        throw errorInfo;
      }

      return errorInfo;
    }
  };
};

/**
 * Handle pre-login errors (errors before user authentication)
 * @param {Error|Object} error - Error object
 * @returns {Object} - Error information
 */
export const handlePreLoginError = (error) => {
  const errorType = getErrorType(error);
  
  // Network errors before login
  if (errorType === ErrorTypes.NETWORK) {
    return {
      type: errorType,
      message: 'لا يمكن الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.',
      shouldShow: true,
      action: 'RETRY',
    };
  }

  // Auth errors before login (normal, don't show)
  if (errorType === ErrorTypes.AUTH) {
    return {
      type: errorType,
      message: null,
      shouldShow: false,
      action: 'REDIRECT_TO_LOGIN',
    };
  }

  // Other errors
  return handleAdvancedError(error, {
    showNotification: false,
    logError: true,
    trackError: true,
  });
};

/**
 * Create error notification payload
 * @param {Object} errorInfo - Error information from handleAdvancedError
 * @returns {Object} - Notification payload
 */
export const createErrorNotification = (errorInfo) => {
  if (!errorInfo.shouldShow) {
    return null;
  }

  return {
    message: errorInfo.message,
    type: 'error',
    duration: errorInfo.type === ErrorTypes.NETWORK ? 0 : 5000, // Network errors stay longer
    action: errorInfo.action,
  };
};

export default {
  ErrorTypes,
  getErrorType,
  shouldShowError,
  handleAdvancedError,
  withErrorHandling,
  handlePreLoginError,
  createErrorNotification,
};

